'use client'

import { FiFilm, FiFileText, FiLayers, FiUser, FiChevronRight, FiChevronDown } from 'react-icons/fi'
import { MdOutlinePermMedia } from 'react-icons/md'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator } from '@/components/ui/shadcn/dropdown-menu'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/shadcn/tooltip'
import { useShow } from '@/contexts/ShowContext'
import { Link } from 'react-router-dom'
import { FaChevronLeft } from 'react-icons/fa'

const NULL_CONTEXT = { segment: null, scene: null, description: null, character: null, mode: null }

function Crumb({ icon: Icon, label, color = 'text-muted-foreground', dim = false }) {
    return (
        <span
            className={`flex items-center select-none gap-1.5 ${color} ${dim ? 'opacity-50' : ''} max-w-[180px] truncate`}
            title={label}
        >
            <Icon className="w-3 h-3 shrink-0" />
            <span className="truncate">{label}</span>
        </span>
    )
}

function Separator() {
    return <FiChevronRight className="w-3 h-3 text-muted-foreground/40 shrink-0" />
}

export default function ToolbarPlugin({
    segments = [],
    activeSegmentId,
    onSegmentChange,
    episode,
    editorContext = NULL_CONTEXT,
    showHeader = true,
    showBackButton = true,
    showCrumbs = true,
    showSegments = true,
    title,
    showAssetsToggle = false,
    assetsVisible = false,
    onToggleAssets,
}) {
    const { show } = useShow()
    const { segment, scene, description, character } = editorContext

    const characterName = character
        ? show?.characters?.find(c => c._id === character.id)?.name ?? character.name
        : null

    const crumbs = []

    if (segment) {
        crumbs.push(
            <Crumb key="segment" icon={FiLayers} label={segment.name} color="text-primary" />
        )
    }

    if (scene) {
        if (crumbs.length > 0) crumbs.push(<Separator key="sep-scene" />)
        crumbs.push(
            <Crumb key="scene" icon={FiFilm} label={scene.name} color="text-secondary-foreground" />
        )
    }

    if (description) {
        if (crumbs.length > 0) crumbs.push(<Separator key="sep-desc" />)
        crumbs.push(
            <Crumb key="description" icon={FiFileText} label="Description" color="text-sky-300" />
        )
    } else if (character) {
        if (crumbs.length > 0) crumbs.push(<Separator key="sep-char" />)
        crumbs.push(
            <Crumb key="character" icon={FiUser} label={characterName || 'Character'} color="text-indigo-300" />
        )
    }

    const displayTitle = title ?? episode?.name ?? 'Episode'
    const segmentSelectVisible = showSegments && segments.length > 0
    const activeSegmentLabel = segments.find((seg) => seg.id === activeSegmentId)?.label ?? 'Entire Episode'

    if (!showHeader && !showCrumbs && !segmentSelectVisible && !showAssetsToggle) {
        return null
    }

    const controlsRowVisible = segmentSelectVisible || showAssetsToggle

    return (
        <div className="flex flex-col border-b border-border">
            {showHeader && (
                <div className='flex items-center h-20'>
                    {showBackButton && (
                        <Link to='/show/episodes' className='text-secondary-foreground hover:text-white cursor-pointer w-10 flex justify-center items-center rounded-md'>
                            <FaChevronLeft className='w-4 h-4' />
                        </Link>
                    )}
                    <h1 className='text-2xl font-bold text-secondary-foreground select-none border-l-4 border-primary pl-4'>{displayTitle}</h1>
                </div>
            )}

            {controlsRowVisible && (
                <div className="flex items-center justify-between gap-2 px-10">
                    <div className="flex items-center gap-2 min-w-0">
                        {segmentSelectVisible && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="select-none inline-flex h-9 w-auto min-w-62 outline-none items-center justify-between gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
                                        <span className="truncate">{activeSegmentLabel}</span>
                                        <FiChevronDown className="w-4 h-4 text-secondary-foreground/60" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-62">
                                    <DropdownMenuRadioGroup
                                        value={activeSegmentId || '__all__'}
                                        onValueChange={(val) => onSegmentChange(val === '__all__' ? null : val)}
                                    >
                                        <DropdownMenuRadioItem value="__all__" className="text-xs h-6">Entire Episode</DropdownMenuRadioItem>
                                        <DropdownMenuSeparator />
                                        {segments.map((seg) => (
                                            <DropdownMenuRadioItem key={seg.id} value={seg.id} className="text-xs h-6">
                                                {seg.label}
                                            </DropdownMenuRadioItem>
                                        ))}
                                    </DropdownMenuRadioGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                    {showAssetsToggle && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={() => onToggleAssets?.()}
                                        aria-pressed={assetsVisible}
                                        className={`inline-flex items-center justify-center h-8 w-8 rounded-md border cursor-pointer transition-colors ${assetsVisible
                                            ? 'bg-primary/10 border-primary/40 text-primary'
                                            : 'bg-background border-border text-secondary-foreground hover:bg-secondary'}`}
                                    >
                                        <MdOutlinePermMedia className="w-4 h-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>{assetsVisible ? 'Hide assets' : 'Show assets'}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            )}

            {showCrumbs && (
                <div className="h-8 flex items-center gap-1.5 min-w-0 px-10 text-muted-foreground/80 text-xs">
                    {crumbs}
                </div>
            )}
        </div>
    )
}
